import * as ibantools from 'ibantools';
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete } from '@mui/joy';

import {
  AtBicValue,
  AuBicValue,
  BeBicValue,
  type BicValueKey,
  ChBicValue,
  DeBicValue,
  DkBicValue,
  EsBicValue,
  FiBicValue,
  FrBicValue,
  GbBicValue,
  IeBicValue,
  LiBicValue,
  LtBicValue,
  LuBicValue,
  McBicValue,
  NlBicValue,
} from '~/common/enums/bicValues';
import { trpc } from '@/config/trpc';

import { removeWhitespaces } from '@/shared/utils/string';

import useEvent from '@/shared/hooks/useEvent';

import FormField, { type FieldProps } from '@/shared/components/2.0/input/FormField';

type BicValueOption = {
  label: string;
  value: BicValueKey;
};

type Props = {
  iban?: string | null;
  value: BicValueKey | null;
  onChange: (value: BicValueKey | null) => void;
  disabled?: boolean;
} & FieldProps;

const SelectBic: React.FC<Props> = ({ disabled, iban, onChange, value, ...fieldProps }) => {
  const [selectedValue, setSelectedValue] = useState<BicValueOption | null>(null);

  const { data: ibanValidationResult } = trpc.finance.iban.validate.useQuery(iban as string, {
    enabled: !!iban && ibantools.isValidIBAN(removeWhitespaces(iban)),
  });

  const ibanCountryCode = useMemo(() => {
    if (iban && iban.length >= 2) {
      return iban.substring(0, 2).toUpperCase();
    }
    setSelectedValue(null);
    return null;
  }, [iban]);

  const bicValueOptions = useMemo(() => {
    switch (ibanCountryCode) {
      case 'AT':
        return Object.values(AtBicValue);
      case 'AU':
        return Object.values(AuBicValue);
      case 'BE':
        return Object.values(BeBicValue);
      case 'CH':
        return Object.values(ChBicValue);
      case 'DE':
        return Object.values(DeBicValue);
      case 'DK':
        return Object.values(DkBicValue);
      case 'ES':
        return Object.values(EsBicValue);
      case 'FI':
        return Object.values(FiBicValue);
      case 'FR':
        return Object.values(FrBicValue);
      case 'GB':
        return Object.values(GbBicValue);
      case 'IE':
        return Object.values(IeBicValue);
      case 'LI':
        return Object.values(LiBicValue);
      case 'LT':
        return Object.values(LtBicValue);
      case 'LU':
        return Object.values(LuBicValue);
      case 'MC':
        return Object.values(McBicValue);
      case 'NL':
        return Object.values(NlBicValue);
      default:
        return [];
    }
  }, [ibanCountryCode]);

  const bicAutoCompleteOptions = useMemo(
    () =>
      bicValueOptions
        .map((bicValue) => ({
          label: bicValue as string,
          value: bicValue,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [bicValueOptions],
  );

  useEffect(() => {
    if (value === selectedValue?.value) {
      return;
    }
    const bicValue = bicAutoCompleteOptions.find((option) => option.value === value);
    setSelectedValue(bicValue ?? null);
  }, [bicAutoCompleteOptions, value]);

  const handleChange = useEvent((option: BicValueOption | null) => {
    setSelectedValue(option);
    onChange(option?.value ?? null);
  });

  useEffect(() => {
    if (!ibanValidationResult?.isValid) {
      return;
    }
    const newBicValue = bicAutoCompleteOptions.find(
      (option) => option.value === ibanValidationResult.bic.bic,
    );
    handleChange(newBicValue ?? null);
  }, [bicAutoCompleteOptions, bicValueOptions, handleChange, ibanValidationResult]);

  return (
    <FormField {...fieldProps}>
      <Autocomplete
        disabled={disabled || !bicAutoCompleteOptions.length}
        onChange={(_, newValue) => handleChange(newValue)}
        value={selectedValue}
        options={bicAutoCompleteOptions}
      />
    </FormField>
  );
};

export default SelectBic;
